<template>
  <RevCard>
    <div class="p-24 md:p-32">
      <h2 class="heading-2 mb-8">
        {{ i18n(translations.title) }}
      </h2>

      <h3 class="body-1">
        {{ i18n(translations.subtitle) }}
      </h3>
    </div>

    <Filters class="px-24 md:px-32" @submit="handleFiltersSubmit" />

    <PaginatedTable
      v-if="!isFetching && inventoryOpportunitiesResponse"
      :caption="
        i18n(translations.inventoryOpportunitiesCount, {
          count: inventoryOpportunitiesResponse?.count ?? 0,
        })
      "
      :columns
      :current-page
      :data="inventoryOpportunitiesResponse"
      :page-size="PAGE_SIZE"
      transparent-header
      @page-change="handlePaginationClick"
    >
      <template #body-ranking="{ item }">
        <span class="mr-6">{{ item.rankingIndex }}</span>

        <Trend
          :has-arrow="true"
          :value="item.quantitySoldEvolutionPercentage"
          with-percentage
        />
      </template>

      <template #body-grade="{ item }">
        <RevTag :label="item.grade.label" variant="secondary" />
      </template>

      <template #body-isListed="{ item }">
        <IconCheckInCircle
          v-if="item.isListed"
          class="text-static-success-hi"
        />
        <IconCrossInCircle
          v-if="!item.isListed"
          class="text-static-danger-hi"
        />
      </template>

      <template #body-quantitySoldInLast30Days="{ item }">
        <div class="grid grid-cols-2 gap-x-8">
          <div class="caption">7 days</div>
          <div class="caption">30 days</div>
          <div class="body-2-bold">
            {{ item.quantitySoldLast7Days || '0' }}
          </div>
          <div class="body-2-bold">
            {{ item.quantitySoldLast30Days || '0' }}
          </div>
        </div>
      </template>

      <template #body-averagePriceLast30Days="{ item }">
        <div class="grid grid-cols-2 gap-x-8 sm:min-w-[10rem]">
          <div class="caption">Minimum</div>
          <div class="caption">Average</div>
          <div class="body-2-bold">
            {{ i18n.price(item.minimumPriceLast30Days) }}
          </div>
          <div class="body-2-bold">
            {{ i18n.price(item.averagePriceLast30Days) }}
          </div>
        </div>
      </template>

      <template #body-estimatedDemand="{ item }">
        <div class="grid grid-cols-1">
          <div class="caption">Quantity</div>
          <div class="body-2-bold">
            {{ item.estimatedDemand }}
          </div>
        </div>
      </template>

      <template #body-competition="{ item }">
        <RevTag
          :label="item.competition.label"
          :variant="getCompetitionVariant(item.competition.value)"
        />
      </template>

      <template #body-actions="{ item }">
        <RevButton
          v-if="item.isListed"
          size="small"
          :to="{
            type: 'internal',
            name: isListingsPageV2Default
              ? ROUTE_NAMES.LISTINGSV2.INDEX
              : ROUTE_NAMES.LISTINGS.INDEX,
            query: { productId: item.productId },
          }"
          variant="secondary"
        >
          {{ i18n(translations.viewListing) }}
        </RevButton>

        <RevButton
          v-if="!item.isListed"
          size="small"
          :to="{
            type: 'internal',
            name: ROUTE_NAMES.LISTINGS.CREATE,
            params: { productId: item.productId },
          }"
          :tracking="{
            name: 'create-listing',
            page_type: 'opportunities_inventory',
            zone: 'inventory-opportunities-dashboard',
            product_id: item.productId,
          }"
          variant="primary"
        >
          {{ i18n(translations.createListing) }}
        </RevButton>
      </template>
    </PaginatedTable>

    <div v-if="isFetching" class="p-24 md:p-32">
      <RevLoadingScreen :text="i18n(translations.loadingText)" />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed } from 'vue'

import { sellerOpportunities } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTag } from '@ds/components/Tag'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'

import Trend from '@/components/Trend/Trend.vue'
import { ROUTE_NAMES } from '~/constants/routes'
import { useListingPagePreference } from '~/scopes/listings/pages/listingsV2/composables/useListingPagePreference'

import { DEFAULT_PAGE, PAGE_SIZE } from './Inventory.constants'
import translations from './Inventory.translations'
import type { FilterValues } from './components/Filters/Filters.types'
import Filters from './components/Filters/Filters.vue'

const i18n = useI18n()
const route = useRoute()
const currentPage = computed(
  () => parseInt(route.query.page?.toString() || '', 10) || DEFAULT_PAGE,
)
const queryParams = computed(() => ({
  page: currentPage.value,
  ...route.query,
}))

const { isListingsPageV2Default } = useListingPagePreference()

const { data: inventoryOpportunitiesResponse, pending: isFetching } =
  await useHttpFetch(sellerOpportunities.getInventoryOpportunities, {
    queryParams,
    watch: [queryParams],
    transform: (data) => ({
      ...data,
      results: data.results?.map((item, index) => ({
        rankingIndex: index + 1,
        ...item,
      })),
    }),
  })

const columns = [
  {
    label: i18n(translations.columnDemandRanking),
    key: 'ranking',
  },
  {
    label: i18n(translations.columnProduct),
    key: 'title',
  },
  {
    label: i18n(translations.columnBrand),
    key: 'brand',
  },
  {
    label: i18n(translations.columnGrade),
    key: 'grade',
  },
  {
    label: i18n(translations.columnCategory),
    key: 'categoryName',
  },
  {
    label: i18n(translations.columnListed),
    key: 'isListed',
  },
  {
    label: i18n(translations.columnQuantity),
    key: 'quantity',
  },
  {
    label: i18n(translations.columnQuantitySold),
    key: 'quantitySoldInLast30Days',
  },
  {
    label: i18n(translations.columnPriceOnPlatform),
    key: 'averagePriceLast30Days',
  },
  {
    label: i18n(translations.columnEstimatedDemand),
    key: 'estimatedDemand',
  },
  {
    label: i18n(translations.columnCompetition),
    key: 'competition',
  },
  {
    label: i18n(translations.columnActions),
    key: 'actions',
  },
]

function getCompetitionVariant(competition: string) {
  if (competition === 'LOW') {
    return 'warning'
  }
  if (competition === 'HIGH') {
    return 'danger'
  }

  return 'success'
}

async function handlePaginationClick({ page }: { page: number }) {
  navigateTo({ ...route, query: { ...queryParams.value, page } })
}

async function handleFiltersSubmit(values: FilterValues) {
  navigateTo({ ...route, query: { ...values }, replace: true })
}
</script>
